import { useState, useEffect } from 'react';
import { Plus, Pin, Trash2, Loader2 } from 'lucide-react';
import notesService from '@services/notes.service';
import Button from '@components/common/Button';
import ConfirmModal from '@components/common/ConfirmModal';

const NOTE_COLORS = [
  { name: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-900' },
  { name: 'pink', bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-900' },
  { name: 'green', bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-900' },
  { name: 'blue', bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-900' },
  { name: 'purple', bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-900' },
  { name: 'orange', bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-900' },
];

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notesService.getAllNotes();
      setNotes(response?.data?.data || []);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    try {
      setIsCreating(true);
      const randomColor = NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)].name;
      const response = await notesService.createNote({
        title: '',
        content: '',
        color: randomColor
      });
      const newNote = response?.data?.data;
      if (newNote) {
        setNotes([newNote, ...notes]);
        setEditingNoteId(newNote.id);
      }
    } catch (err) {
      console.error('Error creating note:', err);
      setError('Failed to create note. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateNote = async (id, updates) => {
    try {
      const response = await notesService.updateNote(id, updates);
      const updatedNote = response?.data?.data;
      if (updatedNote) {
        setNotes(notes.map(note => note.id === id ? updatedNote : note));
      }
    } catch (err) {
      console.error('Error updating note:', err);
      setError('Failed to update note. Please try again.');
    }
  };

  const handleTogglePin = async (id) => {
    try {
      const response = await notesService.togglePin(id);
      const updatedNote = response?.data?.data;
      if (updatedNote) {
        const updatedNotes = notes.map(note => note.id === id ? updatedNote : note);
        updatedNotes.sort((a, b) => {
          if (a.isPinned === b.isPinned) {
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          }
          return b.isPinned ? 1 : -1;
        });
        setNotes(updatedNotes);
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
      setError('Failed to pin/unpin note. Please try again.');
    }
  };

  const handleDeleteNote = (id) => {
    setDeleteTargetId(id);
  };

  const confirmDeleteNote = async () => {
    if (!deleteTargetId) return;
    try {
      setIsDeleting(true);
      await notesService.deleteNote(deleteTargetId);
      setNotes(notes.filter(note => note.id !== deleteTargetId));
      setDeleteTargetId(null);
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const getColorClasses = (colorName) => {
    const color = NOTE_COLORS.find(c => c.name === colorName) || NOTE_COLORS[0];
    return color;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const pinnedNotes = notes.filter(note => note.isPinned);
  const unpinnedNotes = notes.filter(note => !note.isPinned);

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <div className="w-full px-4 sm:px-4 lg:px-4 py-3 sm:py-4 lg:py-3">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-sm sm:text-base text-gray-600">
              Note down your thoughts, goals, and ideas
            </p>
          </div>
          <Button
            onClick={handleCreateNote}
            disabled={isCreating}
            variant="primary"
            className="w-full sm:w-auto"
          >
            {isCreating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            <span className="hidden sm:inline">Add Note</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center min-h-[60vh] w-full">
            <Loader2 className="w-8 h-8 text-navbar animate-spin" />
          </div>
        )}

        {/* Notes Grid */}
        {!loading && (
          <>
            {/* Pinned Notes Section */}
            {pinnedNotes.length > 0 && (
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-gray-700 mb-3">Pinned</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 sm:gap-3">
                  {pinnedNotes.map((note) => {
                    const colorClasses = getColorClasses(note.color);
                    return (
                      <NoteCard
                        key={note.id}
                        note={note}
                        colorClasses={colorClasses}
                        isEditing={editingNoteId === note.id}
                        onEdit={() => setEditingNoteId(note.id)}
                        onBlur={() => setEditingNoteId(null)}
                        onUpdate={handleUpdateNote}
                        onTogglePin={handleTogglePin}
                        onDelete={handleDeleteNote}
                        formatDate={formatDate}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* All Notes Section */}
            {unpinnedNotes.length > 0 && (
              <div>
                {pinnedNotes.length > 0 && (
                  <h2 className="text-lg font-semibold text-gray-700 mb-3">All Notes</h2>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 sm:gap-3">
                  {unpinnedNotes.map((note) => {
                    const colorClasses = getColorClasses(note.color);
                    return (
                      <NoteCard
                        key={note.id}
                        note={note}
                        colorClasses={colorClasses}
                        isEditing={editingNoteId === note.id}
                        onEdit={() => setEditingNoteId(note.id)}
                        onBlur={() => setEditingNoteId(null)}
                        onUpdate={handleUpdateNote}
                        onTogglePin={handleTogglePin}
                        onDelete={handleDeleteNote}
                        formatDate={formatDate}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {notes.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <Plus className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No notes yet</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Click "Add Note" to create your first note
                </p>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteTargetId}
        onClose={() => !isDeleting && setDeleteTargetId(null)}
        onConfirm={confirmDeleteNote}
        title="Delete this note?"
        message="This action cannot be undone. The note will be permanently removed."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={isDeleting}
      />
    </div>
  );
};

const NoteCard = ({
  note,
  colorClasses,
  isEditing,
  onEdit,
  onBlur,
  onUpdate,
  onTogglePin,
  onDelete,
  formatDate
}) => {
  const [title, setTitle] = useState(note.title || '');
  const [content, setContent] = useState(note.content || '');

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleBlur = () => {
    if (title !== note.title || content !== note.content) {
      onUpdate(note.id, { title, content });
    }
    onBlur();
  };

  return (
    <div
      className={`${colorClasses.bg} ${colorClasses.border} border rounded-2xl p-3 sm:p-4 min-h-[180px] sm:min-h-[200px] flex flex-col transition-all hover:shadow-md relative group`}
    >
      {/* Action Buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button
          onClick={() => onTogglePin(note.id)}
          className={`p-1.5 rounded-lg transition-all ${
            note.isPinned
              ? 'bg-white/80 text-gray-700'
              : 'bg-white/60 hover:bg-white/80 text-gray-600'
          }`}
          title={note.isPinned ? 'Unpin note' : 'Pin note'}
        >
          <Pin className={`w-4 h-4 ${note.isPinned ? 'fill-current' : ''}`} />
        </button>
        <button
          onClick={() => onDelete(note.id)}
          className="p-1.5 rounded-lg bg-white/60 hover:bg-white/80 text-red-600 transition-all opacity-0 group-hover:opacity-100"
          title="Delete note"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Note Content */}
      <div className="flex-1 pr-12 sm:pr-16">
        <input
          type="text"
          value={title}
          onChange={handleTitleChange}
          onFocus={onEdit}
          onBlur={handleBlur}
          placeholder="Note title..."
          className={`w-full bg-transparent border-none outline-none font-semibold text-sm sm:text-base ${colorClasses.text} placeholder-gray-500/60 mb-2`}
        />
        <textarea
          value={content}
          onChange={handleContentChange}
          onFocus={onEdit}
          onBlur={handleBlur}
          placeholder="Write something..."
          className={`w-full bg-transparent border-none outline-none resize-none text-xs sm:text-sm ${colorClasses.text} placeholder-gray-500/50 min-h-[60px] sm:min-h-[80px]`}
          rows={4}
        />
      </div>

      {/* Date */}
      <div className="mt-auto pt-2">
        <p className="text-[10px] sm:text-xs text-gray-600/80">
          {formatDate(note.updatedAt)}
        </p>
      </div>
    </div>
  );
};

export default Notes;
